import { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import type { SearchResult } from '../../../client/claims/types.gen';
import type { PartnerSearchResult } from '../SearchView';
import {
  MATCH_ANSWER,
  MATCH_CLAIMCASE,
  MATCH_CLAIMCASE_EMAIL,
  MATCH_CLAIMCASE_POLICY,
  MATCH_EXTERNAL_CLAIMCASE_REF,
  MATCH_FNOL_ANSWER,
  MATCH_FNOL_INSURED_OBJECT,
  MATCH_NOTE,
  MATCH_POLICY,
  MATCH_TASK,
  MATCH_UNLABELED_EMAIL,
} from '../MATCH_CONSTS';

// Not in MATCH_CONSTS (shared with older code); the tags come straight from
// the generated SearchResult union.
const MATCH_CLAIMCASE_HOLDER_NAME = 'MatchClaimcaseHolderName';
const MATCH_CLAIMCASE_OBJECT_NAME = 'MatchClaimcaseObjectName';

type SearchResultRowProps = {
  hit: PartnerSearchResult;
};

type RowItemProps = {
  isHovering: boolean;
  isClickable: boolean;
};

/**
 * Compile-time exhaustiveness check: `result` is `never` here as long as every
 * constructor of SearchResult is handled above. At runtime an unknown tag from
 * a newer backend still gets a placeholder row instead of crashing the view.
 */
const unknownTag = (result: never): string =>
  (result as { tag?: string }).tag ?? 'unknown';

const SearchResultRow = ({ hit }: SearchResultRowProps) => {
  const { frontendBaseUrl, result } = hit;
  const [isMouseOver, setIsMouseOver] = useState(false);
  const navigate = useNavigate();

  const handleMouseEnter = () => setIsMouseOver(true);
  const handleMouseLeave = () => setIsMouseOver(false);

  const openInPartner = (redirect: string) =>
    navigate(
      `/partner?url=${encodeURIComponent(
        frontendBaseUrl
      )}&redirect=${encodeURIComponent(redirect)}`
    );

  const handleClick = () => {
    switch (result.tag) {
      case MATCH_ANSWER:
      case MATCH_CLAIMCASE:
      case MATCH_CLAIMCASE_EMAIL:
      case MATCH_CLAIMCASE_POLICY:
      case MATCH_EXTERNAL_CLAIMCASE_REF:
      case MATCH_CLAIMCASE_HOLDER_NAME:
      case MATCH_CLAIMCASE_OBJECT_NAME:
      case MATCH_NOTE:
      case MATCH_TASK:
        openInPartner(
          `/claim-cases/${result.claimcaseSummaryOut.claimcaseKey}`
        );
        break;
      case MATCH_FNOL_ANSWER:
      case MATCH_FNOL_INSURED_OBJECT:
        openInPartner(`/fnol-inbox?fnolKey=${result.fnolKey}`);
        break;
      case MATCH_UNLABELED_EMAIL:
        openInPartner('/unlabelled-emails');
        break;
      case MATCH_POLICY:
        // The claims app has no page for a policy on its own.
        break;
      default:
        unknownTag(result);
    }
  };

  const cells = (
    claimcase: string,
    insuredObject: string,
    insuredObjectType: string,
    incidentDay: string,
    isClickable = true
  ) => (
    <>
      {[claimcase, insuredObject, insuredObjectType, incidentDay].map(
        (text, index) => (
          <RowItem
            key={index}
            onClick={isClickable ? handleClick : undefined}
            onMouseEnter={isClickable ? handleMouseEnter : undefined}
            onMouseLeave={isClickable ? handleMouseLeave : undefined}
            isHovering={isMouseOver}
            isClickable={isClickable}
          >
            {text}
          </RowItem>
        )
      )}
      <RowBorder />
    </>
  );

  const getResultRow = (result: SearchResult) => {
    switch (result.tag) {
      case MATCH_ANSWER:
      case MATCH_CLAIMCASE:
      case MATCH_CLAIMCASE_EMAIL:
      case MATCH_CLAIMCASE_POLICY:
      case MATCH_EXTERNAL_CLAIMCASE_REF:
      case MATCH_CLAIMCASE_HOLDER_NAME:
      case MATCH_CLAIMCASE_OBJECT_NAME:
      case MATCH_NOTE:
      case MATCH_TASK:
        return cells(
          result.claimcaseSummaryOut.claimcaseKey,
          result.claimcaseSummaryOut.insuredObject.contents,
          result.claimcaseSummaryOut.insuredObject.tag,
          result.claimcaseSummaryOut.incidentDay
        );
      case MATCH_FNOL_ANSWER:
      case MATCH_FNOL_INSURED_OBJECT:
        return cells(
          'FNOL',
          result.insuredObject.contents,
          result.insuredObject.tag,
          result.incidentDay
        );
      case MATCH_POLICY:
        return cells(
          'Policy',
          result.insuredObject.contents,
          result.insuredObject.tag,
          'N/A',
          false
        );
      case MATCH_UNLABELED_EMAIL:
        return cells('Unlabeled email', 'N/A', 'N/A', 'N/A');
      default:
        return (
          <>
            <div>Unknown search result ({unknownTag(result)})</div>
            <div>-</div>
            <div>-</div>
            <div>-</div>
          </>
        );
    }
  };

  return getResultRow(result);
};

export default SearchResultRow;

const RowBorder = styled.div`
  grid-column: 1 / span 4;
  border-bottom: 0.0625rem dotted #dedede;
`;

const RowItem = styled.div<RowItemProps>`
  color: ${({ isHovering }) => (isHovering ? '#fc8e49' : 'inherit')};
  text-decoration: ${({ isHovering }) => (isHovering ? 'underline' : 'none')};
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
`;
